import { useState, useCallback } from 'react';
import { Article } from '../types';

// キャッシュエントリの型定義
interface CacheEntry {
  article: Article;
  processedContent: string;
  timestamp: number;
}

// LRUキャッシュの型定義
interface LRUCache {
  capacity: number;
  size: number;
  cache: Map<string, CacheEntry>;
}

// デフォルトのキャッシュサイズ（MBで指定）
const DEFAULT_CACHE_SIZE_MB = 10;
// 記事データの平均サイズ見積もり（バイト単位）
const AVERAGE_ARTICLE_SIZE_BYTES = 150 * 1024; // 約150KB

/**
 * 文字列のバイトサイズを計算する
 */
const getByteSize = (str: string): number => {
  // UTF-16エンコードの文字列をバイト数に変換（近似値）
  return new Blob([str]).size;
};

/**
 * 記事とその加工済みコンテンツをキャッシュするためのカスタムフック
 * メモリ使用量を制限するLRU(Least Recently Used)キャッシュを実装
 */
export const useArticleCache = (maxCacheSizeMB = DEFAULT_CACHE_SIZE_MB) => {
  // キャッシュの最大サイズ（バイト単位）
  const maxCacheBytes = maxCacheSizeMB * 1024 * 1024;
  
  // キャッシュの初期化
  const [cache, setCache] = useState<LRUCache>({
    capacity: maxCacheBytes,
    size: 0,
    cache: new Map<string, CacheEntry>()
  });

  /**
   * キャッシュにエントリを追加する
   */
  const setArticleCache = useCallback((slug: string, article: Article, processedContent: string) => {
    setCache(prevCache => {
      const newCache = { ...prevCache };
      
      // 既存のエントリがあれば削除して、そのサイズを引く
      if (newCache.cache.has(slug)) {
        const oldEntry = newCache.cache.get(slug)!;
        const oldSize = getByteSize(JSON.stringify(oldEntry.article)) + 
                        getByteSize(oldEntry.processedContent);
        newCache.size -= oldSize;
      }
      
      // 新しいエントリのサイズを計算
      const newEntrySize = getByteSize(JSON.stringify(article)) + 
                          getByteSize(processedContent);
      
      // キャッシュがいっぱいの場合、古いエントリから削除
      if (newCache.size + newEntrySize > newCache.capacity) {
        // 最も古いエントリを特定して削除
        let oldestTimestamp = Date.now();
        let oldestKey = '';
        
        // MapのエントリをArray.fromで配列に変換してから反復処理
        Array.from(newCache.cache.entries()).forEach(([key, entry]) => {
          if (entry.timestamp < oldestTimestamp) {
            oldestTimestamp = entry.timestamp;
            oldestKey = key;
          }
        });
        
        if (oldestKey) {
          const removedEntry = newCache.cache.get(oldestKey)!;
          const removedSize = getByteSize(JSON.stringify(removedEntry.article)) + 
                             getByteSize(removedEntry.processedContent);
          newCache.size -= removedSize;
          newCache.cache.delete(oldestKey);
        }
      }
      
      // サイズが大きすぎる場合は、さらに古いエントリを削除
      while (newCache.size + newEntrySize > newCache.capacity && newCache.cache.size > 0) {
        let oldestTimestamp = Date.now();
        let oldestKey = '';
        
        // MapのエントリをArray.fromで配列に変換してから反復処理
        Array.from(newCache.cache.entries()).forEach(([key, entry]) => {
          if (entry.timestamp < oldestTimestamp) {
            oldestTimestamp = entry.timestamp;
            oldestKey = key;
          }
        });
        
        if (oldestKey) {
          const removedEntry = newCache.cache.get(oldestKey)!;
          const removedSize = getByteSize(JSON.stringify(removedEntry.article)) + 
                             getByteSize(removedEntry.processedContent);
          newCache.size -= removedSize;
          newCache.cache.delete(oldestKey);
        } else {
          // 削除するエントリがもうない場合は中断
          break;
        }
      }
      
      // それでもサイズが大きすぎる場合（単一の記事が巨大な場合）
      if (newEntrySize > newCache.capacity) {
        console.warn(`Article is too large to cache: ${slug}`);
        return prevCache; // 変更せずに終了
      }
      
      // 新しいエントリを追加
      newCache.cache.set(slug, {
        article,
        processedContent,
        timestamp: Date.now()
      });
      newCache.size += newEntrySize;
      
      return newCache;
    });
  }, []);

  /**
   * キャッシュからエントリを取得する
   */
  const getArticleCache = useCallback((slug: string): { article: Article, processedContent: string } | null => {
    const entry = cache.cache.get(slug);
    
    if (!entry) {
      return null;
    }
    
    // エントリが見つかった場合はタイムスタンプを更新
    setCache(prevCache => {
      const newCache = { ...prevCache };
      newCache.cache.set(slug, {
        ...entry,
        timestamp: Date.now()
      });
      return newCache;
    });
    
    return {
      article: entry.article,
      processedContent: entry.processedContent
    };
  }, [cache]);

  /**
   * キャッシュをクリアする
   */
  const clearCache = useCallback(() => {
    setCache({
      capacity: maxCacheBytes,
      size: 0,
      cache: new Map<string, CacheEntry>()
    });
  }, [maxCacheBytes]);
  
  /**
   * 現在のキャッシュ状態を取得する（デバッグ用）
   */
  const getCacheStats = useCallback(() => {
    return {
      entryCount: cache.cache.size,
      totalSizeBytes: cache.size,
      totalSizeMB: Math.round((cache.size / (1024 * 1024)) * 100) / 100,
      capacityMB: Math.round((cache.capacity / (1024 * 1024)) * 100) / 100
    };
  }, [cache]);

  return {
    setArticleCache,
    getArticleCache,
    clearCache,
    getCacheStats
  };
}; 